import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import { markdownItWikiEmbeds } from './MarkdownItWikiEmbeds.js'
import { markdownItWikilinks } from './MarkdownItWikilinks.js'
import { markdownItAutoImage } from './MarkdownItAutoImage.js'
import { slugify as toSlug, extractFrontmatter } from './LinkParser.js'
import {
  getMarkdownPostProcessors,
  getMarkdownCodeBlockProcessors,
  createProcessorContext
} from './PluginAPI.js'
import { setFormPluginConfig, ViewMode } from '../plugins/quasarForms/index.js'

const WIKI_PIPE_TOKEN = '__KB_PIPE__'

const protectWikiPipes = (input) => {
  const src = String(input || '')
  if (!src.includes('[[') || !src.includes('|')) return src

  let out = ''
  let i = 0
  let inFence = false
  let fenceChar = ''
  let fenceLen = 0
  let inInline = false
  let inlineLen = 0
  let lineStart = true

  const isFenceStart = (idx, ch) => {
    if (ch !== '`' && ch !== '~') return 0
    let j = idx
    while (j < src.length && src[j] === ch) j++
    const len = j - idx
    return len >= 3 ? len : 0
  }

  while (i < src.length) {
    const ch = src[i]

    if (lineStart && !inInline) {
      let j = i
      let spaces = 0
      while (j < src.length && spaces < 3 && src[j] === ' ') {
        j++
        spaces++
      }
      const fenceCh = src[j]
      if (!inFence) {
        const len = isFenceStart(j, fenceCh)
        if (len) {
          inFence = true
          fenceChar = fenceCh
          fenceLen = len
          out += src.slice(i, j + len)
          i = j + len
          lineStart = false
          continue
        }
      } else if (fenceCh === fenceChar) {
        const len = isFenceStart(j, fenceCh)
        if (len && len >= fenceLen) {
          inFence = false
          fenceChar = ''
          fenceLen = 0
          out += src.slice(i, j + len)
          i = j + len
          lineStart = false
          continue
        }
      }
    }

    if (!inFence) {
      if (ch === '`') {
        let j = i
        while (j < src.length && src[j] === '`') j++
        const len = j - i
        if (inInline) {
          if (len === inlineLen) {
            inInline = false
            inlineLen = 0
          }
        } else {
          inInline = true
          inlineLen = len
        }
        out += src.slice(i, j)
        i = j
        lineStart = false
        continue
      }

      if (!inInline && ch === '[' && src[i + 1] === '[') {
        let j = i + 2
        while (j < src.length) {
          if (src[j] === ']' && src[j + 1] === ']') break
          j++
        }
        if (j < src.length) {
          const inner = src.slice(i + 2, j)
          const replaced = inner.split('|').join(WIKI_PIPE_TOKEN)
          out += '[[' + replaced + ']]'
          i = j + 2
          lineStart = false
          continue
        }
      }
    }

    out += ch
    lineStart = ch === '\n'
    i++
  }

  return out
}

const wrapInlineIframes = (input) => {
  const src = String(input || '')
  if (!src.includes('<iframe')) return src

  let out = ''
  let i = 0
  let inFence = false
  let fenceChar = ''
  let fenceLen = 0
  let inInline = false
  let inlineLen = 0
  let lineStart = true

  const isFenceStart = (idx, ch) => {
    if (ch !== '`' && ch !== '~') return 0
    let j = idx
    while (j < src.length && src[j] === ch) j++
    const len = j - idx
    return len >= 3 ? len : 0
  }

  while (i < src.length) {
    const ch = src[i]

    if (lineStart && !inInline) {
      let j = i
      let spaces = 0
      while (j < src.length && spaces < 3 && src[j] === ' ') {
        j++
        spaces++
      }
      const fenceCh = src[j]
      if (!inFence) {
        const len = isFenceStart(j, fenceCh)
        if (len) {
          inFence = true
          fenceChar = fenceCh
          fenceLen = len
          out += src.slice(i, j + len)
          i = j + len
          lineStart = false
          continue
        }
      } else if (fenceCh === fenceChar) {
        const len = isFenceStart(j, fenceCh)
        if (len && len >= fenceLen) {
          inFence = false
          fenceChar = ''
          fenceLen = 0
          out += src.slice(i, j + len)
          i = j + len
          lineStart = false
          continue
        }
      }
    }

    if (!inFence) {
      if (ch === '`') {
        let j = i
        while (j < src.length && src[j] === '`') j++
        const len = j - i
        if (inInline) {
          if (len === inlineLen) {
            inInline = false
            inlineLen = 0
          }
        } else {
          inInline = true
          inlineLen = len
        }
        out += src.slice(i, j)
        i = j
        lineStart = false
        continue
      }

      // Wrap raw iframe HTML tags so linkify doesn't alter src attributes
      if (!inInline && src.slice(i, i + 7).toLowerCase() === '<iframe') {
        const closeIdx = src.toLowerCase().indexOf('</iframe>', i)
        if (closeIdx !== -1) {
          const end = closeIdx + 9
          const tag = src.slice(i, end)
          out += `\n\`\`\`iframe\n${tag}\n\`\`\`\n`
          i = end
          lineStart = true
          continue
        }
        const selfCloseIdx = src.indexOf('/>', i)
        if (selfCloseIdx !== -1) {
          const end = selfCloseIdx + 2
          const tag = src.slice(i, end)
          out += `\n\`\`\`iframe\n${tag}\n\`\`\`\n`
          i = end
          lineStart = true
          continue
        }
      }
    }

    out += ch
    lineStart = ch === '\n'
    i++
  }

  return out
}

// Singleton Markdown-It instance configured similar to current editor
// Enable HTML so native tags like <details>/<summary> render in preview.
// We sanitize the rendered HTML below to strip unsafe content.
const md = new MarkdownIt({ linkify: true, breaks: true, html: true })
// Rule order is critical:
// 1) Embeds FIRST so ![[...]] is consumed before wikilinks or linkify
md.use(markdownItWikiEmbeds, {
  resolveUrl: (target) => {
    // Support http(s)/data and simple bare filenames as /images/{name}
    const t = String(target || '').trim()
    if (/^(https?:|data:)/i.test(t)) return t
    if (/^[^\s\/]+\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(t)) {
      return `/images/${t}`
    }
    return ''
  }
})
// 2) Wikilinks AFTER embeds so [[...]] runs when not preceded by '!'
md.use(markdownItWikilinks)
// 3) Convert bare image URLs to <img> after linkify created <a> tokens
md.use(markdownItAutoImage)

// Add target _blank and rel attributes for external links
const isExternalHref = (href) => /^(?:https?:)\/\//i.test(String(href || ''))
const ensureAttr = (token, name, value) => {
  const idx = token.attrIndex(name)
  if (idx < 0) token.attrPush([name, value])
  else token.attrs[idx][1] = value
}
const origLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options))
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  try {
    const token = tokens[idx]
    const href = token.attrGet && token.attrGet('href')
    if (href && isExternalHref(href)) {
      ensureAttr(token, 'target', '_blank')
      // Security best practice for new tabs
      const existingRel = token.attrGet && token.attrGet('rel')
      const nextRel = existingRel ? `${existingRel} noopener noreferrer` : 'noopener noreferrer'
      ensureAttr(token, 'rel', nextRel)
    }
  } catch {}
  return origLinkOpen(tokens, idx, options, env, self)
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function renderMarkdownToHtml(markdown) {
  try {
    const src = wrapInlineIframes(protectWikiPipes(markdown))
    const raw = md.render(src)
    // Sanitize while allowing interactive-but-safe tags used by plugins
    const out = DOMPurify.sanitize(raw, {
      // Keep common extra tags our plugins rely on
      ADD_TAGS: [
        'div',
        'span',
        'details',
        'summary',
        'figure',
        'figcaption',
        'iframe',
        'video',
        'audio',
        'source',
        'label',
        'input'
      ],
      ADD_ATTR: [
        'class',
        'id',
        'name',
        'value',
        'type',
        'checked',
        'disabled',
        'width',
        'height',
        'allow',
        'allowfullscreen',
        'frameborder',
        'sandbox',
        'referrerpolicy',
        'loading',
        'title',
        'aria-expanded',
        'target',
        'rel',
        'data-*'
      ],
      ALLOW_DATA_ATTR: true,
      FORBID_TAGS: ['script', 'style'],
      // Disallow javascript: URLs inside attributes
      SAFE_FOR_TEMPLATES: true
    })
    if (process.env && process.env.NODE_ENV !== 'production') {
      try {
        console.debug('[KB Markdown] render ok', { inLen: src.length, outLen: out.length })
      } catch {}
    }
    return out
  } catch (e) {
    if (process.env && process.env.NODE_ENV !== 'production') {
      try {
        console.warn('[KB Markdown] render error', e)
      } catch {}
    }
    return '<p></p>'
  }
}

function extractLanguageFromCodeNode(codeEl) {
  if (!codeEl || !codeEl.className) return ''
  const classes = String(codeEl.className || '').split(/\s+/)
  for (const cls of classes) {
    if (cls.startsWith('language-')) return cls.slice('language-'.length).toLowerCase()
    if (cls.startsWith('lang-')) return cls.slice('lang-'.length).toLowerCase()
  }
  return ''
}

function splitIframeFenceSource(source) {
  if (!source) return null
  // Only attempt when another fence marker appears inside the iframe block.
  if (!/^\s*```/m.test(source)) return null
  const lines = String(source).split(/\r?\n/)
  const segments = []
  let current = []
  const fenceRe = /^\s*`{3,}.*$/

  const pushCurrent = () => {
    if (!current.length) return
    const joined = current.join('\n').replace(/\s+$/, '')
    if (joined.trim().length) segments.push(joined)
    current = []
  }

  for (const line of lines) {
    if (fenceRe.test(line)) {
      pushCurrent()
      continue
    }
    current.push(line)
  }
  pushCurrent()

  return segments.length > 1 ? segments : null
}

// Applies code block processors and post processors to the rendered root element
export async function applyProcessors(rootEl, { docId, context = {} } = {}) {
  if (!rootEl) return

  // Configure form plugin state based on context
  const viewMode = context.viewMode || (context.isPublicView ? ViewMode.PUBLIC : ViewMode.PREVIEW)
  const canEdit = context.canEdit !== undefined ? context.canEdit : !context.isPublicView
  setFormPluginConfig({
    viewMode,
    canEdit,
    noteId: docId || context.noteId || null
  })

  const ctx = createProcessorContext({
    docId,
    viewMode,
    canEdit,
    ...context
  })

  if (typeof window !== 'undefined' && window.__KB_DISABLE_PP) {
    if (process.env && process.env.NODE_ENV !== 'production') {
      try {
        console.warn('[KB Markdown] processors disabled via __KB_DISABLE_PP')
      } catch {}
    }
    return
  }

  // 1) Code block processors: replace <pre><code class="language-xxx">...</code></pre>
  const codeBlocks = Array.from(rootEl.querySelectorAll('pre > code'))
  for (const code of codeBlocks) {
    const pre = code.parentElement
    const lang = extractLanguageFromCodeNode(code)
    if (!lang) continue
    const processors = getMarkdownCodeBlockProcessors(lang)
    if (!processors.length) continue
    const source = code.textContent || ''
    // If an iframe fence accidentally swallowed subsequent fences, split it here.
    const iframeSegments = lang === 'iframe' ? splitIframeFenceSource(source) : null
    if (iframeSegments && pre && pre.parentElement) {
      const frag = document.createDocumentFragment()
      for (const seg of iframeSegments) {
        const container = document.createElement('div')
        container.className = `kb-codeblock kb-codeblock-${lang}`
        for (const entry of processors) {
          try {
            await entry.handler(seg, container, ctx)
          } catch {
            /* noop */
          }
        }
        frag.appendChild(container)
      }
      pre.parentElement.replaceChild(frag, pre)
      continue
    }

    const container = document.createElement('div')
    container.className = `kb-codeblock kb-codeblock-${lang}`
    for (const entry of processors) {
      try {
        await entry.handler(source, container, ctx)
      } catch {
        /* noop */
      }
    }
    if (pre && pre.parentElement) {
      pre.parentElement.replaceChild(container, pre)
    }
  }

  // Heading anchors: assign id attributes based on text so subpath links can scroll
  const headings = Array.from(rootEl.querySelectorAll('h1, h2, h3, h4, h5, h6'))
  for (const h of headings) {
    try {
      const text = (h.textContent || '').trim()
      if (!text) continue
      const id = 'h-' + toSlug(text)
      if (!h.id) h.id = id
    } catch {}
  }

  // 2) Markdown post processors: give plugins a chance to transform the DOM
  const post = getMarkdownPostProcessors()
  for (const entry of post) {
    try {
      await entry.handler(rootEl, ctx)
    } catch {
      /* noop */
    }
  }
}

export async function renderMarkdownInto(rootEl, markdown, { docId, context = {} } = {}) {
  if (!rootEl) return
  // Render HTML first; never throw from this step
  // Strip YAML frontmatter for preview rendering so meta does not show after paste/switch
  let bodyOnly = ''
  try {
    const fm = extractFrontmatter(markdown)
    bodyOnly = fm && typeof fm === 'object' ? fm.body || '' : String(markdown || '')
  } catch {
    bodyOnly = String(markdown || '')
  }
  const html = renderMarkdownToHtml(bodyOnly)
  // Mark this root as a KB preview root so post-processors can detect preview-only context
  try {
    rootEl.setAttribute('data-kb-preview', '1')
  } catch {}
  rootEl.innerHTML = html
  if (process.env && process.env.NODE_ENV !== 'production') {
    try {
      const sample = (html || '').slice(0, 120).replace(/\n/g, '\\n')
      console.debug('[KB Markdown] innerHTML applied', { outLen: html.length, sample })
    } catch {}
  }
  // Dev-only fallback visualization if HTML ended up empty unexpectedly
  if (
    process.env &&
    process.env.NODE_ENV !== 'production' &&
    (!html || !html.length) &&
    markdown &&
    String(markdown).length
  ) {
    try {
      rootEl.innerHTML = `<pre class="kb-debug-preview">${escapeHtml(markdown)}</pre>`
      console.warn('[KB Markdown] empty HTML fallback applied for debugging')
    } catch {}
  }
  try {
    await applyProcessors(rootEl, { docId, context })
  } catch (e) {
    if (process.env && process.env.NODE_ENV !== 'production') {
      try {
        console.warn('[MarkdownRendererBridge] applyProcessors error', e)
      } catch {}
    }
  }
  // Clean up preview marker
  try {
    rootEl.removeAttribute('data-kb-preview')
  } catch {}
}

export default { renderMarkdownInto, renderMarkdownToHtml, applyProcessors }
